// IndexedDB adapter (ADR 0008, idb 8). One database per origin+brand+user+workspace partition, so switching
// account/workspace never exposes another partition's cached data. Schema upgrades are explicit ordered steps
// and must preserve the outbox (pending user edits).
import { openDB, deleteDB, type DBSchema, type IDBPDatabase, type IDBPTransaction, type StoreNames } from 'idb';
import type { OutboxRecord } from '@/sync/ops';
import type { ListRecord, ProjectRecord, TaskRecord } from '@/sync/diff';

export interface Partition { brandId: string; userId: string; workspaceId: string }

export interface TaskPanesDB extends DBSchema {
  meta: { key: string; value: unknown };
  projects: { key: string; value: ProjectRecord };
  lists: { key: string; value: ListRecord; indexes: { byProject: string } };
  tasks: { key: string; value: TaskRecord; indexes: { byProject: string } };
  outbox: { key: number; value: OutboxRecord; indexes: { byOperation: string; byState: string } };
  conflicts: { key: string; value: { id: string; at: number; operation_id: string; entity_type: string; entity_id: string; local: unknown; server: unknown; resolved: boolean } };
  fileQueue: { key: string; value: FileQueueRecord; indexes: { byTask: string } };
}

/** Queued attachment upload (bytes kept on device) or removal (P12). */
export interface FileQueueRecord {
  id: string; kind: 'upload' | 'delete'; taskId: string; name: string; size: number; sha256: string;
  /** file bytes (ArrayBuffer: structured-clone safe across browsers) and type, null for removals */
  bytes: ArrayBuffer | null; mime: string;
  state: 'queued' | 'uploading' | 'rejected'; attempts: number; next_attempt_at: number; created_at: number; last_error?: string;
  /** v7: uploaded as an image of the task's notes */
  role?: 'note_image' | 'comment_image' | 'project_comment_image';
}

export const SCHEMA_VERSION = 2;
type UpgradeTx = IDBPTransaction<TaskPanesDB, StoreNames<TaskPanesDB>[], 'versionchange'>;

/** Ordered upgrade steps. Step N migrates version N-1 → N. Never drop or rewrite the outbox store. */
export const MIGRATIONS: Array<(db: IDBPDatabase<TaskPanesDB>, tx: UpgradeTx) => void> = [
  // v1: initial schema
  db => {
    db.createObjectStore('meta');
    db.createObjectStore('projects', { keyPath: 'id' });
    db.createObjectStore('lists', { keyPath: 'id' }).createIndex('byProject', 'projectId');
    db.createObjectStore('tasks', { keyPath: 'id' }).createIndex('byProject', 'projectId');
    const ob = db.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
    ob.createIndex('byOperation', 'op.operation_id', { unique: true });
    ob.createIndex('byState', 'state');
    db.createObjectStore('conflicts', { keyPath: 'id' });
  },
  // v2 (P12): offline attachment queue. Additive: the outbox and all entity stores are untouched.
  db => {
    db.createObjectStore('fileQueue', { keyPath: 'id' }).createIndex('byTask', 'taskId');
  },
];

export function partitionName(p: Partition, origin = location.origin): string {
  const safe = (s: string) => encodeURIComponent(s);
  return `taskpanes|${safe(origin)}|b:${safe(p.brandId)}|u:${safe(p.userId)}|w:${safe(p.workspaceId)}`;
}

/**
 * Opens the partition's database behind a stable handle. Browsers can terminate an IndexedDB connection abnormally
 * (installed web apps after sleep or long background: "Connection to Indexed Database server lost", "The database
 * connection is closing"); the handle then reopens the database (idb `terminated`), so every holder (store, sync
 * engine, file queue) keeps working without a page reload. `tpReopen()` forces/awaits a reopen (used by retries).
 */
export async function openLocalDb(p: Partition, migrations = MIGRATIONS, origin?: string): Promise<IDBPDatabase<TaskPanesDB>> {
  let cur: IDBPDatabase<TaskPanesDB>;
  let reopening: Promise<void> | null = null;
  const reopen = (): Promise<void> => (reopening ??= open().then(d => { cur = d; }).finally(() => { reopening = null; }));
  function open(): Promise<IDBPDatabase<TaskPanesDB>> {
    return openDB<TaskPanesDB>(partitionName(p, origin), migrations.length, {
      upgrade(db, oldVersion, newVersion, tx) {
        for (let v = oldVersion; v < (newVersion ?? migrations.length); v++) migrations[v]!(db, tx);
      },
      blocked() { /* another tab holds an older version; it receives versionchange and closes */ },
      // This tab runs an older schema while a newer tab wants to upgrade: surface "update ready" (no forced reload).
      blocking() { if (typeof window !== 'undefined') window.dispatchEvent(new Event('tp-db-blocking')); },
      terminated() { console.warn('[tp-db] connection terminated by the browser; reopening'); void reopen(); },
    });
  }
  cur = await open();
  return new Proxy({} as IDBPDatabase<TaskPanesDB>, {
    get(_t, k) {
      if (k === 'tpReopen') return reopen;
      // Some browsers never fire `terminated`: a transaction refused on a closed connection starts the reopen, so the
      // next attempt (sync engine retry, store retry) succeeds.
      if (k === 'transaction') return (...a: unknown[]) => { try { return (cur.transaction as (...x: unknown[]) => unknown).apply(cur, a); } catch (e) { if (isConnectionLost(e)) void reopen(); throw e; } };
      const v = (cur as unknown as Record<string | symbol, unknown>)[k];
      return typeof v === 'function' ? (v as (...a: unknown[]) => unknown).bind(cur) : v;
    },
  });
}

/** A failure caused by a lost/closing connection (not by the data): reopen and retry is safe. */
export function isConnectionLost(e: unknown): boolean {
  const err = e as { name?: string; message?: string } | null;
  return err?.name === 'InvalidStateError' || err?.name === 'UnknownError' || /connection|closing|server lost/i.test(err?.message || '');
}

/** Reopens a handle from openLocalDb (no-op for plain connections, e.g. in tests). */
export async function reopenLocalDb(db: unknown): Promise<void> {
  const r = (db as { tpReopen?: () => Promise<void> }).tpReopen;
  if (typeof r === 'function') await r();
}

export async function deletePartition(p: Partition, origin?: string): Promise<void> {
  await deleteDB(partitionName(p, origin));
}

/** Recognises storage quota failures across browsers. */
export function isQuotaError(e: unknown): boolean {
  const err = e as { name?: string; message?: string; inner?: { name?: string } } | null;
  const name = err?.name || err?.inner?.name || '';
  return name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED' || /quota/i.test(err?.message || '');
}

export class LocalStorageFullError extends Error {
  constructor(public override readonly cause: unknown) { super('Device storage is full. Your change was not saved.'); this.name = 'LocalStorageFullError'; }
}
